import { cardPayOnlineLabel, qrPayLabel, tt } from '@agos/shared'

/**
 * The three report tabs the owner asked for — FRONTEND.md §9.251, FB-411.
 *
 * **Every field name here was read off the running API**, not inferred from the endpoint's name: the
 * shapes are recorded in FB-411 and the sums are all the server's. Nothing in this file computes a
 * total, which is what keeps these tabs from disagreeing with *Štatistiky* the way §9.211 refused to
 * let a second list do.
 *
 * Money arrives as a **decimal number of euros**, not cents and not a string — measured
 * (`revenue: 6377.9`) — so it is converted once, here, rather than at each of the fifteen places it is
 * printed.
 */

/** Legacy's `DaysSelector` options, `reports.tsx:1356`. */
export const DAY_WINDOWS = [7, 14, 30, 60, 90, 365] as const
export const CUSTOMER_DAY_WINDOWS = [30, 60, 90, 180, 365] as const

function euros(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) : 0
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

export interface SalesCut {
  key: string
  label: string
  orderCount: number
  revenueCents: number
  /** Only the channel cut carries one; the server sends it and nothing else needs it. */
  avgOrderCents: number | null
}

export interface SalesReport {
  byChannel: SalesCut[]
  byPayment: SalesCut[]
  byWeekday: SalesCut[]
  byHour: SalesCut[]
  /** Whether the window holds anything at all — the empty state's own question. */
  isEmpty: boolean
}

const WEEKDAYS = [
  tt('Nedeľa'),
  tt('Pondelok'),
  tt('Utorok'),
  tt('Streda'),
  tt('Štvrtok'),
  tt('Piatok'),
  tt('Sobota'),
]

/**
 * The methods by the values the server sends - FB-1052, §9.741.
 *
 * `sales-stats__byPaymentMethod.sql` groups by the column itself, so a method arrives as `card_terminal`,
 * never `cardTerminal`. This map was keyed the camelCased way - the financial summary's `byPayment` keys -
 * and so on the owner's data, where cash and the three cards are the only four values, every card row
 * printed its raw key and only *Hotovosť* was named: the one key both spellings share. Its `online` key
 * went with it, a value no writer stores. `card` is legacy's own older value, which the backend's splits
 * still count with the online card; `qr_pay` is a door QR payment since BACKEND.md §10A.390.
 */
const PAYMENTS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta terminál'),
  card_courier: tt('Karta kuriér'),
  // §9.767 - the online card's one name; it said *Karta online*.
  card_online: cardPayOnlineLabel(),
  card: tt('Karta'),
  qr_pay: qrPayLabel(),
}

export function salesReportOf(payload: unknown): SalesReport {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const list = (key: string): Record<string, unknown>[] =>
    Array.isArray(row[key]) ? (row[key] as Record<string, unknown>[]) : []

  const byChannel = list('byType').map((entry) => ({
    // §9.94 — a channel is called what the franchise calls it. `channelName` is on the wire; the
    // slug is the fallback, never a hard-coded translation of it.
    key: text(entry.type) ?? '',
    label: text(entry.channelName) ?? text(entry.type) ?? tt('Neurčené'),
    orderCount: count(entry.orderCount),
    revenueCents: euros(entry.revenue),
    avgOrderCents: euros(entry.avgOrder),
  }))

  const answered = list('byPaymentMethod').map((entry) => {
    const method = text(entry.method) ?? ''
    return {
      key: method,
      label: PAYMENTS[method] ?? (method === '' ? tt('Neurčené') : method),
      orderCount: count(entry.orderCount),
      revenueCents: euros(entry.revenue),
      avgOrderCents: null,
    }
  })
  /* §9.767, owner O129 - a CardPay online and a QR Pay row always, a nought included: the server groups by the
   * method column, so a method nobody paid with in the window has no row, and the owner asked to see the two side by
   * side wherever the online card is counted. Only on a window that answered methods at all, so an empty window
   * stays empty (`isEmpty` below reads the server's lists, not these). */
  const nought = (method: string): SalesCut => ({
    key: method,
    label: PAYMENTS[method] ?? method,
    orderCount: 0,
    revenueCents: 0,
    avgOrderCents: null,
  })
  const byPayment =
    answered.length === 0
      ? answered
      : [
          ...answered,
          ...['card_online', 'qr_pay']
            .filter((method) => !answered.some((cut) => cut.key === method))
            .map(nought),
        ]

  const byWeekday = list('byDayOfWeek')
    // The server answers `dow` 0–6 in whatever order the group by produced; a week reads in order.
    .slice()
    .sort((left, right) => count(left.dow) - count(right.dow))
    .map((entry) => ({
      key: String(count(entry.dow)),
      label: WEEKDAYS[count(entry.dow)] ?? tt('Neurčené'),
      orderCount: count(entry.orderCount),
      revenueCents: euros(entry.revenue),
      avgOrderCents: null,
    }))

  const byHour = list('byHour')
    .slice()
    .sort((left, right) => count(left.hour) - count(right.hour))
    .map((entry) => ({
      key: String(count(entry.hour)),
      // `08:00`, so an hour reads as a time rather than as a number on a bar.
      label: `${String(count(entry.hour)).padStart(2, '0')}:00`,
      orderCount: count(entry.orderCount),
      revenueCents: euros(entry.revenue),
      avgOrderCents: null,
    }))

  return {
    byChannel,
    byPayment,
    byWeekday,
    byHour,
    isEmpty:
      byChannel.length === 0 &&
      byPayment.length === 0 &&
      byWeekday.length === 0 &&
      byHour.length === 0,
  }
}

export interface TopProduct {
  productId: number
  name: string
  categoryName: string | null
  quantity: number
  revenueCents: number
}

/** `GET /reports/top-products` answers a **bare array**, which is why this takes one. */
export function topProductsOf(payload: unknown): TopProduct[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const name = text(row.name)
    if (name === null) return []
    return [
      {
        productId: count(row.productId),
        name,
        categoryName: text(row.categoryName),
        quantity: count(row.totalQty),
        revenueCents: euros(row.revenue),
      },
    ]
  })
}

export interface CustomerInsights {
  uniqueCustomers: number
  returningCustomers: number
  totalOrders: number
  avgOrderCents: number
  /** Percent, as the server computes it — `61.4` on this data. */
  returningPercent: number
  topSpenders: {
    phone: string
    name: string
    orderCount: number
    totalSpentCents: number
    lastOrderAt: string | null
  }[]
}

export function customerInsightsOf(payload: unknown): CustomerInsights {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const summary = (typeof row.summary === 'object' && row.summary !== null ? row.summary : {}) as Record<
    string,
    unknown
  >
  const spenders = Array.isArray(row.topSpenders) ? (row.topSpenders as Record<string, unknown>[]) : []
  return {
    uniqueCustomers: count(summary.uniqueCustomers),
    returningCustomers: count(summary.returningCustomers),
    totalOrders: count(summary.totalOrders),
    avgOrderCents: euros(summary.avgOrderValue),
    returningPercent:
      typeof summary.returningPct === 'number' && Number.isFinite(summary.returningPct)
        ? summary.returningPct
        : 0,
    topSpenders: spenders.map((entry) => ({
      phone: text(entry.phone) ?? '',
      // The dump holds blank and whitespace names; a row with none is still a customer who spent.
      name: text(entry.name) ?? tt('Bez mena'),
      orderCount: count(entry.orderCount),
      totalSpentCents: euros(entry.totalSpent),
      lastOrderAt: text(entry.lastOrderAt),
    })),
  }
}

/**
 * Why a window is empty — §9.251, and FB-260's rule.
 *
 * The restored dump's newest order is **2026-07-28**, so thirty days answers nothing and the screen
 * reads as broken. Thirty days is right for a restaurant that is trading; what is wrong is a blank
 * panel that does not say which of the two it is looking at.
 */
export function emptyWindowNote(days: number): string {
  return `${tt('Za posledných')} ${days} ${tt('dní nie sú žiadne objednávky. Skúste dlhšie obdobie.')}`
}
